import { FC, useRef, useMemo } from "react";
import "./OptInsFormV2.scss";
import {
  CoreFieldValue,
  FieldFormV2,
  FieldFormV2Module,
  FieldV2,
  LoadingV2,
  SecondaryCard,
  SecondaryCardHeader,
  useFieldSchemaV2FactoryContext,
} from "@ucl/library";
import { useFinancialProducts } from "@/hooks/useFinancialProducts";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { createOptInsFormBuilder } from "./OptInsFormBuilder";
import { FIELD_KEYS } from "@/constants/fieldKeys";
import { useOptIns } from "@/hooks/useOptIns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldHalved, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

export interface OptInsFormV2Props {
  financialApplicationId: string;
  depositApplicationId: string;
  validationErrors: Record<string, string[]>;
  onBlur?: (field: CoreFieldValue) => void;
  isInitiallyCollapsed?: boolean;
  allowChangingWhetherCollapsed?: boolean;
  readOnly?: boolean;
  // Configuration for optional opt-in services
  showCdars?: boolean;
  showInsuredCashSweep?: boolean;
}

export const OptInsFormV2: FC<OptInsFormV2Props> = (props) => {
  const fieldFormModule = useRef<FieldFormV2Module<any> | null>(null);

  const { optInsValue, setOptInsValue, saveOptInsData } = useOptIns(
    props.financialApplicationId,
    props.depositApplicationId
  );

  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { products } = useFinancialProducts(financialApplicationApiClient);

  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  const formBuilder = useMemo(() => {
    if (!schemaFactory) return undefined;
    return createOptInsFormBuilder(schemaFactory);
  }, [schemaFactory]);

  // Get the product for the deposit application
  const product = products.find((p) => p.id === optInsValue?.financialProductId);

  // Helper function to check if an opt-in is enabled
  const isOptInEnabled = (optInKey: string): boolean => {
    if (!optInsValue) return false;

    switch (optInKey) {
      case "e-statements":
        return optInsValue.eStatementsOptIn?.isEStatementsOptInEnabled ?? true; // Default to true for e-statements
      case "checks":
        return optInsValue.checksOptIn?.isChecksOptInEnabled ?? false;
      case "wires":
        return optInsValue.wiresOptIn?.isWiresOptInEnabled ?? false;
      case "ach":
        return optInsValue.achOptIn?.isAchOptInEnabled ?? false;
      case "positive-pay":
        return optInsValue.positivePayOptIn?.isPositivePayOptInEnabled ?? false;
      case "remote-deposit":
        return optInsValue.remoteDepositScannerOptIn?.isRemoteDepositScannerOptInEnabled ?? false;
      case "cdars":
        return optInsValue.cdarsOptIn?.isCdarsOptInEnabled ?? false;
      case "insured-cash-sweep":
        return optInsValue.insuredCashSweepOptIn?.isInsuredCashSweepOptInEnabled ?? false;
      default:
        return false;
    }
  };

  if (!schemaFactory || !formBuilder || !optInsValue || !product) {
    return (
      <section className="opt-ins-form-v2">
        <div className="opt-ins-form-v2__loading">
          <LoadingV2 />
        </div>
      </section>
    );
  }

  return (
    <section className="opt-ins-form-v2">
      <SecondaryCard
        hideBody={props.isInitiallyCollapsed ?? false}
        isCollapsible={props.allowChangingWhetherCollapsed ?? true}
        headerContent={
          <>
            <div className="opt-ins-form-v2__header-main">
              <SecondaryCardHeader label={product.productName} />
              {props.validationErrors && Object.keys(props.validationErrors).length > 0 && (
                <span className="opt-ins-form-v2__validation-errors e-badge e-badge-pill">
                  {Object.keys(props.validationErrors).length}
                </span>
              )}
            </div>
            <div className="opt-ins-form-v2__header-summary">
              {product.hasEStatementsOptIn && (
                <span className="opt-ins-form-v2__header-summary-item">
                  E-Statements
                  <FontAwesomeIcon
                    icon={isOptInEnabled("e-statements") ? faCheck : faTimes}
                    className={isOptInEnabled("e-statements") ? "enabled" : "disabled"}
                  />
                </span>
              )}
              {product.hasChecksOptIn && (
                <span className="opt-ins-form-v2__header-summary-item">
                  Checks
                  <FontAwesomeIcon
                    icon={isOptInEnabled("checks") ? faCheck : faTimes}
                    className={isOptInEnabled("checks") ? "enabled" : "disabled"}
                  />
                </span>
              )}
              {product.hasWiresOptIn && (
                <span className="opt-ins-form-v2__header-summary-item">
                  Wires
                  <FontAwesomeIcon
                    icon={isOptInEnabled("wires") ? faCheck : faTimes}
                    className={isOptInEnabled("wires") ? "enabled" : "disabled"}
                  />
                </span>
              )}
              {product.hasAchOptIn && (
                <span className="opt-ins-form-v2__header-summary-item">
                  ACH
                  <FontAwesomeIcon
                    icon={isOptInEnabled("ach") ? faCheck : faTimes}
                    className={isOptInEnabled("ach") ? "enabled" : "disabled"}
                  />
                </span>
              )}
              {product.hasPositivePayOptIn && (
                <span className="opt-ins-form-v2__header-summary-item">
                  Positive Pay
                  <FontAwesomeIcon
                    icon={isOptInEnabled("positive-pay") ? faCheck : faTimes}
                    className={isOptInEnabled("positive-pay") ? "enabled" : "disabled"}
                  />
                </span>
              )}
              {product.hasRemoteDepositScannerOptIn && (
                <span className="opt-ins-form-v2__header-summary-item">
                  Remote Deposit Scanner
                  <FontAwesomeIcon
                    icon={isOptInEnabled("remote-deposit") ? faCheck : faTimes}
                    className={isOptInEnabled("remote-deposit") ? "enabled" : "disabled"}
                  />
                </span>
              )}
              {props.showCdars && product.hasCdarsOptIn && (
                <span className="opt-ins-form-v2__header-summary-item">
                  CDARS
                  <FontAwesomeIcon
                    icon={isOptInEnabled("cdars") ? faCheck : faTimes}
                    className={isOptInEnabled("cdars") ? "enabled" : "disabled"}
                  />
                </span>
              )}
              {props.showInsuredCashSweep && product.hasInsuredCashSweepOptIn && (
                <span className="opt-ins-form-v2__header-summary-item">
                  Insured Cash Sweep
                  <FontAwesomeIcon
                    icon={isOptInEnabled("insured-cash-sweep") ? faCheck : faTimes}
                    className={isOptInEnabled("insured-cash-sweep") ? "enabled" : "disabled"}
                  />
                </span>
              )}
            </div>
          </>
        }
      >
        <div className="opt-ins-form-v2__content">
          <FieldFormV2<any>
            ref={fieldFormModule}
            value={optInsValue}
            fieldSchemaFormBuilder={formBuilder}
            onFormSubmit={async (value) => {
              await saveOptInsData(value);
            }}
            onFieldChanged={(newValue, newFieldValue, fieldProps) => {
              setOptInsValue(newValue, newFieldValue, fieldProps);
            }}
            errors={props.validationErrors}
          >
            <div className="opt-ins-form-v2__services">
              <h4>
                <FontAwesomeIcon
                  icon={faShieldHalved}
                  style={{ marginRight: "0.5rem", color: "var(--primary-color)" }}
                />
                Account Services
              </h4>
              <div className="opt-ins-form-v2__service-fields">
                {/* E-Statements */}
                {product.hasEStatementsOptIn && (
                  <div className="opt-ins-form-v2__service-item">
                    <FieldV2
                      fieldProps={FIELD_KEYS.DEPOSIT_APPLICATION.E_STATEMENTS_OPT_IN}
                      complexFieldValue={{
                        readOnly: true, // E-statements should always be disabled since it defaults to true
                      }}
                      handleOnBlur={props.onBlur}
                    />
                  </div>
                )}

                {/* Checks */}
                {product.hasChecksOptIn && (
                  <div className="opt-ins-form-v2__service-item">
                    <FieldV2
                      fieldProps={FIELD_KEYS.DEPOSIT_APPLICATION.CHECKS_OPT_IN}
                      complexFieldValue={{
                        readOnly: props.readOnly,
                      }}
                      handleOnBlur={props.onBlur}
                    />
                  </div>
                )}

                {/* Wires */}
                {product.hasWiresOptIn && (
                  <div className="opt-ins-form-v2__service-item">
                    <FieldV2
                      fieldProps={FIELD_KEYS.DEPOSIT_APPLICATION.WIRES_OPT_IN}
                      complexFieldValue={{
                        readOnly: props.readOnly,
                      }}
                      handleOnBlur={props.onBlur}
                    />
                  </div>
                )}

                {/* ACH */}
                {product.hasAchOptIn && (
                  <div className="opt-ins-form-v2__service-item">
                    <FieldV2
                      fieldProps={FIELD_KEYS.DEPOSIT_APPLICATION.ACH_OPT_IN}
                      complexFieldValue={{
                        readOnly: props.readOnly,
                      }}
                      handleOnBlur={props.onBlur}
                    />
                  </div>
                )}

                {/* Positive Pay */}
                {product.hasPositivePayOptIn && (
                  <div className="opt-ins-form-v2__service-item">
                    <FieldV2
                      fieldProps={FIELD_KEYS.DEPOSIT_APPLICATION.POSITIVE_PAY_OPT_IN}
                      complexFieldValue={{
                        readOnly: props.readOnly,
                      }}
                      handleOnBlur={props.onBlur}
                    />
                  </div>
                )}

                {/* Remote Deposit Scanner */}
                {product.hasRemoteDepositScannerOptIn && (
                  <div className="opt-ins-form-v2__service-item">
                    <FieldV2
                      fieldProps={FIELD_KEYS.DEPOSIT_APPLICATION.REMOTE_DEPOSIT_SCANNER_OPT_IN}
                      complexFieldValue={{
                        readOnly: props.readOnly,
                      }}
                      handleOnBlur={props.onBlur}
                    />
                  </div>
                )}

                {/* CDARS */}
                {props.showCdars && product.hasCdarsOptIn && (
                  <div className="opt-ins-form-v2__service-item">
                    <FieldV2
                      fieldProps={FIELD_KEYS.DEPOSIT_APPLICATION.CDARS_OPT_IN}
                      complexFieldValue={{
                        readOnly: props.readOnly,
                      }}
                      handleOnBlur={props.onBlur}
                    />
                  </div>
                )}

                {/* Insured Cash Sweep */}
                {props.showInsuredCashSweep && product.hasInsuredCashSweepOptIn && (
                  <div className="opt-ins-form-v2__service-item">
                    <FieldV2
                      fieldProps={FIELD_KEYS.DEPOSIT_APPLICATION.INSURED_CASH_SWEEP_OPT_IN}
                      complexFieldValue={{
                        readOnly: props.readOnly,
                      }}
                      handleOnBlur={props.onBlur}
                    />
                  </div>
                )}
              </div>
            </div>
          </FieldFormV2>
        </div>
      </SecondaryCard>
    </section>
  );
};
